import { useQuery } from '@tanstack/react-query';
import { lmsApi } from '@/shared/api/lms';
import { useSessionStore } from '@/entities/session';

/** LMS-роли: student=пользователь, teacher=преподаватель, curator=менеджер группы, admin_lms=администратор. methodist — контент. */
export type LmsRole = 'student' | 'teacher' | 'curator' | 'methodist' | 'admin_lms';

export function lmsRoleLabelKey(role: LmsRole): `learning.roles.${LmsRole}` {
  return `learning.roles.${role}`;
}

export const LMS_ROLES: readonly LmsRole[] = [
  'student',
  'teacher',
  'curator',
  'methodist',
  'admin_lms',
] as const;

const LMS_STAFF_ROLES: readonly LmsRole[] = ['teacher', 'methodist', 'curator', 'admin_lms'];

/** sub из JWT access_token — ключ кеша /lms/me при смене пользователя в том же браузере. */
export function accessTokenSub(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { sub?: string };
    return json.sub ?? null;
  } catch {
    return null;
  }
}

// useLmsMe — единый источник LMS-ролей текущего пользователя.
export function useLmsMe() {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  const userKey = accessTokenSub() ?? (isAuthenticated ? 'unknown' : 'guest');
  return useQuery({
    queryKey: ['lms', 'me', userKey],
    queryFn: () => lmsApi.me(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

export function hasAnyRole(roles: string[] | undefined, ...required: LmsRole[]): boolean {
  if (!roles || roles.length === 0) return false;
  if (roles.includes('admin_lms')) return true;
  for (const r of required) {
    if (roles.includes(r)) return true;
  }
  return false;
}

/** Преподаватель / методист / куратор / admin_lms — планирование Telemost и админ-редактор. */
export function isLmsStaff(roles: string[] | undefined): boolean {
  return hasAnyRole(roles, ...LMS_STAFF_ROLES);
}
